// components/integrations/CallSystemIntegration.jsx
import React, { useState, useEffect } from "react";
import {
  Phone,
  Check,
  RefreshCw,
  Link,
  Unlink,
  AlertCircle,
  Clock,
  TrendingUp,
  Calendar,
  Zap,
} from "lucide-react";
import apiService from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

const CallSystemIntegration = () => {
  const { isDarkMode } = useTheme();
  const [integration, setIntegration] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [callSystemUrl, setCallSystemUrl] = useState("");
  const [error, setError] = useState("");
  const [syncDays, setSyncDays] = useState(30);

  useEffect(() => {
    fetchIntegration();
    fetchSyncStatus();
  }, []);

  useEffect(() => {
    let interval;
    if (syncStatus?.appointmentBookedSyncStatus === "in_progress") {
      interval = setInterval(() => {
        fetchSyncStatus();
      }, 3000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [syncStatus?.appointmentBookedSyncStatus]);

  const fetchIntegration = async () => {
    try {
      setLoading(true);
      const response = await apiService.getCallSystemIntegration();
      if (response.success) {
        setIntegration(response.integration);
        if (response.integration) {
          setCallSystemUrl(response.integration.callSystemUrl);
        }
      }
    } catch (error) {
      console.error("Error fetching integration:", error);
      setError("Failed to load integration status");
    } finally {
      setLoading(false);
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const response = await apiService.getAppointmentBookedSyncStatus();
      if (response.success) {
        setSyncStatus(response);
      }
    } catch (error) {
      console.error("Error fetching sync status:", error);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    setError("");

    try {
      const response = await apiService.connectCallSystem({
        callSystemUrl: callSystemUrl || "http://localhost:5000",
      });

      if (response.success && response.authorizationUrl) {
        window.location.href = response.authorizationUrl;
      } else {
        setError("Failed to initiate connection");
      }
    } catch (err) {
      console.error("Connect error:", err);
      setError(
        err.response?.data?.message || "Failed to connect to call system",
      );
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (
      !window.confirm(
        "Are you sure you want to disconnect the call system integration?",
      )
    ) {
      return;
    }

    try {
      const response = await apiService.disconnectCallSystem();
      if (response.success) {
        await fetchIntegration();
        await fetchSyncStatus();
        setError("");
      }
    } catch (error) {
      console.error("Error disconnecting:", error);
      setError("Failed to disconnect integration");
    }
  };

  const handleSyncAppointmentBookedCalls = async () => {
    try {
      setSyncing(true);
      setError("");

      const response = await apiService.syncAppointmentBookedCalls(syncDays);

      if (response.success) {
        fetchSyncStatus();
      } else {
        setError("Failed to start sync");
      }
    } catch (error) {
      console.error("Sync error:", error);
      setError(error.response?.data?.message || "Failed to start sync");
    } finally {
      setSyncing(false);
    }
  };

  const getSyncStatusDisplay = () => {
    if (!syncStatus) return null;

    switch (syncStatus.appointmentBookedSyncStatus) {
      case "in_progress":
        return {
          color: "blue",
          icon: RefreshCw,
          text: "Syncing in progress...",
          spinning: true,
        };
      case "completed":
        return {
          color: "green",
          icon: Check,
          text: "Sync completed",
          spinning: false,
        };
      case "failed":
        return {
          color: "red",
          icon: AlertCircle,
          text: "Sync failed",
          spinning: false,
        };
      default:
        return {
          color: "gray",
          icon: Clock,
          text: "No sync performed yet",
          spinning: false,
        };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw
          className={`animate-spin ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          size={24}
        />
      </div>
    );
  }

  const statusDisplay = getSyncStatusDisplay();

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div
        className={`${isDarkMode ? "bg-[#13161B] border-[#22262F]" : "bg-white border-gray-200"} border rounded-xl p-6`}
      >
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
              <Phone size={24} className="text-white" />
            </div>
            <div>
              <h3
                className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Call Recording System
              </h3>
              <p
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} mt-1`}
              >
                Sync appointment-booked calls with your CRM
              </p>
            </div>
          </div>

          {integration?.isActive && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/10 border border-green-500/30 rounded-full">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span className="text-xs font-medium text-green-400">
                Connected
              </span>
            </div>
          )}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-2">
            <AlertCircle
              size={16}
              className="text-red-400 flex-shrink-0 mt-0.5"
            />
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        {/* Not Connected State */}
        {!integration?.isActive && (
          <div>
            <p
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-4`}
            >
              Enter your Call Recording System URL to begin the connection.
            </p>

            <div className="space-y-3">
              <div>
                <label
                  className={`block text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1`}
                >
                  Call System URL
                </label>
                <input
                  type="url"
                  value={callSystemUrl}
                  onChange={(e) => setCallSystemUrl(e.target.value)}
                  placeholder="http://localhost:5000 or https://calls.example.com"
                  disabled={connecting}
                  className={`w-full px-3 py-2 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500" : "bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400"} border rounded-lg text-sm focus:border-blue-500 focus:outline-none disabled:opacity-50`}
                />
              </div>

              <button
                onClick={handleConnect}
                disabled={connecting || !callSystemUrl}
                className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                <Link size={16} />
                {connecting ? "Connecting..." : "Connect Call System"}
              </button>
            </div>
          </div>
        )}

        {/* Connected State */}
        {integration?.isActive && (
          <div className="space-y-6">
            {/* Connection Details */}
            <div
              className={`${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-gray-50 border-gray-200"} border rounded-lg p-4`}
            >
              <div className="flex items-center justify-between mb-2">
                <span
                  className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Connected to:
                </span>
                <span
                  className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"} font-mono`}
                >
                  {integration.callSystemUrl}
                </span>
              </div>
              {integration.connectedAt && (
                <div className="flex items-center justify-between">
                  <span
                    className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Connected since:
                  </span>
                  <span
                    className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                  >
                    {new Date(integration.connectedAt).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>

            {/* Sync Status */}
            {syncStatus && (
              <div
                className={`${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-gray-50 border-gray-200"} border rounded-lg p-4`}
              >
                <h4
                  className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"} mb-3`}
                >
                  Last Sync Status
                </h4>

                <div className="flex items-center gap-2 mb-3">
                  {statusDisplay && (
                    <>
                      <statusDisplay.icon
                        size={16}
                        className={`text-${statusDisplay.color}-400 ${statusDisplay.spinning ? "animate-spin" : ""}`}
                      />
                      <span
                        className={`text-sm font-medium text-${statusDisplay.color}-400`}
                      >
                        {statusDisplay.text}
                      </span>
                    </>
                  )}
                </div>

                {syncStatus.lastAppointmentBookedSyncStats &&
                  syncStatus.appointmentBookedSyncStatus === "completed" && (
                    <div className="bg-green-500/5 border border-green-500/20 rounded-lg p-3">
                      <div className="grid grid-cols-3 gap-3 mb-2">
                        <div>
                          <div
                            className={`${isDarkMode ? "text-gray-400" : "text-gray-500"} text-xs mb-1`}
                          >
                            Calls Synced
                          </div>
                          <div
                            className={`${isDarkMode ? "text-white" : "text-gray-900"} text-lg font-semibold flex items-center gap-1`}
                          >
                            <TrendingUp size={14} className="text-blue-400" />
                            {syncStatus.lastAppointmentBookedSyncStats.synced}
                          </div>
                        </div>
                        <div>
                          <div
                            className={`${isDarkMode ? "text-gray-400" : "text-gray-500"} text-xs mb-1`}
                          >
                            Matched
                          </div>
                          <div className="text-green-400 text-lg font-semibold">
                            {syncStatus.lastAppointmentBookedSyncStats.matched}
                          </div>
                        </div>
                        <div>
                          <div
                            className={`${isDarkMode ? "text-gray-400" : "text-gray-500"} text-xs mb-1`}
                          >
                            Skipped
                          </div>
                          <div
                            className={`${isDarkMode ? "text-gray-400" : "text-gray-500"} text-lg font-semibold`}
                          >
                            {syncStatus.lastAppointmentBookedSyncStats.skipped}
                          </div>
                        </div>
                      </div>
                      {syncStatus.appointmentBookedSyncCompletedAt && (
                        <div
                          className={`mt-3 pt-3 border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                        >
                          <div
                            className={`${isDarkMode ? "text-gray-400" : "text-gray-500"} text-xs flex items-center gap-1`}
                          >
                            <Calendar size={12} />
                            Completed:{" "}
                            {new Date(
                              syncStatus.appointmentBookedSyncCompletedAt,
                            ).toLocaleString()}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                {syncStatus.appointmentBookedSyncStatus === "in_progress" && (
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                    <p className="text-sm text-blue-400">
                      Syncing appointment-booked calls... This may take a few
                      minutes.
                    </p>
                  </div>
                )}

                {syncStatus.appointmentBookedSyncStatus === "failed" && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                    <p className="text-sm text-red-400">
                      {syncStatus.appointmentBookedSyncError ||
                        "Sync failed. Please try again."}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Sync Appointment-Booked Calls */}
            <div
              className={`${isDarkMode ? "bg-[#0C0E12] border-green-500/30" : "bg-gray-50 border-green-500/30"} border rounded-lg p-4`}
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
                  <Zap size={20} className="text-green-400" />
                </div>
                <div className="flex-1">
                  <h4
                    className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"} mb-1`}
                  >
                    Sync Appointment-Booked Calls
                  </h4>
                  <p
                    className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Fetch calls where an appointment was booked and match them
                    to your leads, contacts, and appointments.
                  </p>
                </div>
              </div>

              <div className="mb-4">
                <label
                  className={`block text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-2`}
                >
                  Sync calls from the last:
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={syncDays}
                    onChange={(e) =>
                      setSyncDays(
                        Math.max(
                          1,
                          Math.min(90, parseInt(e.target.value) || 30),
                        ),
                      )
                    }
                    min="1"
                    max="90"
                    disabled={syncing}
                    className={`w-20 px-3 py-2 ${isDarkMode ? "bg-[#13161B] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"} border rounded-lg text-sm focus:border-green-500 focus:outline-none disabled:opacity-50`}
                  />
                  <span
                    className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    days
                  </span>
                </div>
              </div>

              <button
                onClick={handleSyncAppointmentBookedCalls}
                disabled={
                  syncing ||
                  syncStatus?.appointmentBookedSyncStatus === "in_progress"
                }
                className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                <RefreshCw
                  size={16}
                  className={
                    syncing ||
                    syncStatus?.appointmentBookedSyncStatus === "in_progress"
                      ? "animate-spin"
                      : ""
                  }
                />
                {syncing ||
                syncStatus?.appointmentBookedSyncStatus === "in_progress"
                  ? "Syncing..."
                  : "Sync Appointment-Booked Calls"}
              </button>
            </div>

            {/* Disconnect Button */}
            <button
              onClick={handleDisconnect}
              className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-red-600/10 text-red-400 border border-red-600/30 rounded-lg hover:bg-red-600/20 transition-colors font-medium"
            >
              <Unlink size={16} />
              Disconnect Integration
            </button>
          </div>
        )}
      </div>

      {/* Information Panel */}
      <div
        className={`${isDarkMode ? "bg-[#13161B] border-[#22262F]" : "bg-white border-gray-200"} border rounded-xl p-6`}
      >
        <h4
          className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"} mb-3`}
        >
          How It Works
        </h4>
        <div className="space-y-2 text-sm text-gray-400">
          <div className="flex items-start gap-2">
            <Check size={16} className="text-green-400 flex-shrink-0 mt-0.5" />
            <span className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
              Fetches all calls where an appointment was booked from your call
              system
            </span>
          </div>
          <div className="flex items-start gap-2">
            <Check size={16} className="text-green-400 flex-shrink-0 mt-0.5" />
            <span className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
              Matches calls to leads and contacts by phone number
            </span>
          </div>
          <div className="flex items-start gap-2">
            <Check size={16} className="text-green-400 flex-shrink-0 mt-0.5" />
            <span className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
              Links call data to the most recent appointment for the matched
              entity
            </span>
          </div>
          <div className="flex items-start gap-2">
            <Check size={16} className="text-green-400 flex-shrink-0 mt-0.5" />
            <span className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
              Stores full transcripts, insights, and qualification data
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallSystemIntegration;
